import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { LookupFlow, CompanyInfo, VIEWS } from '../../index.mjs';
import { NeuroMessage, useUser, useNeuroState, useDispatch } from '@ignitesales/neuro-application/src/neuro-guide';
import { searchForCompany, searchForContact } from '../../../actions/commerce-business-branch-actions.mjs';

const Lookup = ({ appkey, placeholder, value: { value = {} }, valueChanged }) => {
  const { uuid, getEmployee } = useUser();
  const localAnswersKey = `${uuid}-localAnswers`;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const existingLookupInfo = {
    company: JSON.parse(
      window.sessionStorage.getItem(localAnswersKey) || '{}'
    ).companyLookup?.value,
    contact: JSON.parse(
      window.sessionStorage.getItem(localAnswersKey) || '{}'
    ).contactLookup?.value,
  };

  const [lookupInfo, setLookupInfo] = useState(existingLookupInfo);
  const { salesForceSearchResults, guideConfigurations, employeeLoginState } = useNeuroState();
  const [results, setResults] = useState(salesForceSearchResults);
  const [salesforceId, setSalesforceId] = useState(undefined);
  const [hasLoggedIn, setHasLoggedIn] = useState(false);
  const isCompanyLookup = appkey === 'companyLookup';
  const isMockMode = guideConfigurations.isMockMode;
  const [viewType, setViewType] = useState(isCompanyLookup ? VIEWS.COMPANY_RESULTS : VIEWS.CONTACT_RESULTS);

  useEffect(() => {
    setResults(salesForceSearchResults);
  }, [salesForceSearchResults]);

  const autoContactSearch = id => {
    searchForContact(id, uuid, getEmployee(), isMockMode)(dispatch);
    setIsModalOpen(true);
  }

  useEffect(() => {
    ignite.employeeLoginService.shouldEmployeeLogin().then(({ isLoggedIn }) => {
      setHasLoggedIn(isLoggedIn);
      if(!isCompanyLookup) {
        ignite.answerService.getAnswer('salesforceId').then(salesforceAnswer => {
          const id = salesforceAnswer && salesforceAnswer.value?.[0];
          setSalesforceId(id);
          if(isLoggedIn && !isModalOpen && !lookupInfo.contact){
            autoContactSearch(id);
          }
        });
      }
    });
  }, [employeeLoginState?.loginComplete]);

  useEffect(() => {
    if (isCompanyLookup) {
      const input = document.getElementById('lookupInput');
      isCompanyLookup && input.addEventListener('keyup', event => {
        if (event.key === 'Enter') {
          event.preventDefault();
          document.getElementById('lookupSubmit').click();
        }
      });
    }
  }, []);

  const onClick = () => {
    isCompanyLookup ?
      searchForCompany(inputValue?.trim(), uuid, isMockMode)(dispatch) :
      searchForContact(salesforceId, uuid, getEmployee(), isMockMode)(dispatch);
    setIsModalOpen(true);
  };

  const dispatch = useDispatch();
  const cssBEM = 'lookup';

  const onClose = (view) => {
    switch (view) {
      case VIEWS.ADD_CONTACT:
      case VIEWS.CONTACT_RESULTS:
      case VIEWS.NO_CONTACT:
        if(isCompanyLookup){
          valueChanged({});
          setLookupInfo({
            contact: undefined,
            company: undefined,
          });
        }
        break;
    };
    setIsModalOpen(false);
  };

  const onChange = data => {
    isCompanyLookup ? setLookupInfo({ company: data }) : setLookupInfo({ contact: data });
    window.sessionStorage.setItem(
      localAnswersKey,
      JSON.stringify({
        [appkey]: {
          appkey,
          timestamp: new Date(),
          value: data,
        },
      })
    );
    const {accountId, contactId } = data;

    const saveAnswer = (appkey, value) => {
      ignite.answerService.saveAnswer({
        appkey,
        value,
        timestamp: new Date()
      });
    };
    (accountId || salesforceId) && saveAnswer('salesforceId', accountId || salesforceId);
    contactId ? saveAnswer('contactId', contactId) : ignite.answerService.removeAnswer('contactId');

    data.email && valueChanged(true);
  };

  const onContactSearch = data => {
    searchForContact(data.accountId, uuid, getEmployee(), isMockMode)(dispatch);
  };

  const onEditBtnClick = () => {
    const view = isCompanyLookup ? VIEWS.EDIT_COMPANY : VIEWS.EDIT_CONTACT ;
    setViewType(view);
    setIsModalOpen(true);
  }

  const renderSearchStatus = () => ((hasLoggedIn && salesforceId) || isCompanyLookup) ? <NeuroMessage id={'lookupFlow.search'} /> : <i className="fa fa-spinner fa-pulse fa-2x"></i>;

  const isSearchDisabled = isCompanyLookup ? (!(inputValue?.trim()) || !hasLoggedIn) : (!hasLoggedIn || !salesforceId);

  return (
    <div>
      {isCompanyLookup && <input id="lookupInput" className={`${cssBEM}__input`} placeholder={placeholder} type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} validateOnBlur />}
      <button id="lookupSubmit" className={classNames(`${cssBEM}__submit`, 'btn')} disabled={isSearchDisabled} onClick={onClick}>
        { renderSearchStatus() }
      </button>
      <LookupFlow results={results} accountIdParam={results?.accountId} onContactSearch={onContactSearch} isOpen={isModalOpen} onClose={onClose} value={lookupInfo.company || lookupInfo.contact || value} valueChanged={onChange} type={viewType} />
      <CompanyInfo 
        data={lookupInfo.company || lookupInfo.contact}
        isCompany={isCompanyLookup}
        isContact={!isCompanyLookup}
        onClick={onEditBtnClick}
      />
    </div>
  );
};

Lookup.propTypes = {
  actions: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  valueChanged: PropTypes.func,
  value: PropTypes.object,
};

export default Lookup;